// Reprend la logique de `gg_pheno_gantt()` côté R : à partir des points de stade
// (un point par BBCH x YearGroup), on construit des segments stade->stade avec
// une durée en jours, pour un affichage en Gantt horizontal.
 
// Couleurs de début/fin du dégradé — une couleur DISTINCTE est générée par
// interpolation continue pour chaque étape (au lieu de piocher dans une
// liste fixe, qui produisait des doublons dès qu'il y avait plus d'étapes
// que de couleurs dans la liste).
const VERT_CLAIR = [0xc7, 0xe9, 0xc0];
const VERT_FONCE = [0x00, 0x44, 0x1b];
 
function interpolateColor(t) {
  const mix = (a, b) => Math.round(a + (b - a) * t);
  const [r, g, b] = [
    mix(VERT_CLAIR[0], VERT_FONCE[0]),
    mix(VERT_CLAIR[1], VERT_FONCE[1]),
    mix(VERT_CLAIR[2], VERT_FONCE[2]),
  ];
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
}
 
function dayIndex(dateStr) {
  const d = new Date(dateStr);
  const start = new Date(d.getFullYear(), 0, 1);
  return Math.round((d - start) / 86400000);
}
 
function luminance(hex) {
  const v = hex.match(/\w\w/g).map((h) => parseInt(h, 16));
  return (0.299 * v[0] + 0.587 * v[1] + 0.114 * v[2]) / 255;
}
 
/**
 * phenoData: [{YearGroup, BBCH, JourJulien, date_calendaire, Libelle_SP}]
 * -> { rows: [{YearGroup, offset, segments:[{etape, duree, colorHex, colorTexte}]}], etapes: [...] }
 */
export function buildGanttSegments(phenoData) {
  const byYear = new Map();
  for (const row of phenoData) {
    if (!byYear.has(row.YearGroup)) byYear.set(row.YearGroup, []);
    byYear.get(row.YearGroup).push(row);
  }
 
  const yearGroups = Array.from(byYear.keys()).sort((a, b) => a - b);
  const etapesSet = new Set();
  const perYearSegments = new Map();
 
  for (const yg of yearGroups) {
    const pts = byYear.get(yg).sort((a, b) => a.JourJulien - b.JourJulien);
    const segs = [];
    for (let i = 0; i < pts.length - 1; i++) {
      const cur = pts[i];
      const next = pts[i + 1];
      const etape = `${cur.Libelle_SP} → ${next.Libelle_SP}`;
      const duree = dayIndex(next.date_calendaire) - dayIndex(cur.date_calendaire);
      segs.push({ etape, duree, start: dayIndex(cur.date_calendaire) });
      etapesSet.add(etape);
    }
    perYearSegments.set(yg, segs);
  }
 
  const etapes = Array.from(etapesSet);
  const palette = etapes.map((_, i) => interpolateColor(i / Math.max(etapes.length - 1, 1)));
  const colorByEtape = Object.fromEntries(etapes.map((e, i) => [e, palette[i]]));
 
  const rows = yearGroups.map((yg) => {
    const segs = perYearSegments.get(yg);
    return {
      YearGroup: yg,
      offset: segs.length ? segs[0].start : 0,
      segments: segs.map((s) => ({
        ...s,
        colorHex: colorByEtape[s.etape],
        colorTexte: luminance(colorByEtape[s.etape]) > 0.6 ? "#111" : "#fff",
      })),
    };
  });
 
  return { rows, etapes, colorByEtape };
}